/*
  TRINA TURK — CAROUSEL SCROLL BAR
  A hairline under a product carousel: 1px track, 2px brand-navy bar that moves as the row scrolls.
  The bar length shows how much of the row is on screen. Drag it, or click the line, to move the row.
  When everything fits (nothing to scroll), the bar AND that row's prev/next arrows hide, so we never
  show controls that do nothing.

  Loaded site-wide from layout/theme.liquid.

  Where bars come from:
  - Product page (You May Also Like, Complete the Look): rendered in the section by
    snippets/tt-carousel-scrollbar.liquid.
  - Homepage featured-collections carousels: attached by autoAttach() below, so the large homepage
    section file doesn't need editing. Those bars line up with the section's header text.

  Spacing (everywhere): 19px from the bottom of the cards to the line, 14px below the line, and the
  section's bottom padding drops to 7px while a bar shows, so white space never stacks up.
*/
(function () {
  if (window.customElements.get('tt-scrollbar')) return;

  /* ---------- Styles (one copy for the whole site) ---------- */
  var css = [
    'tt-scrollbar{display:block;position:relative;height:28px;margin-block-start:5px;cursor:pointer;touch-action:none;-webkit-tap-highlight-color:transparent;user-select:none}',
    'tt-scrollbar[hidden]{display:none!important}',
    'tt-scrollbar .tt-scrollbar__track{position:absolute;left:0;right:0;top:14px;height:1px;background:rgba(4,30,58,.14)}',
    'tt-scrollbar .tt-scrollbar__thumb{position:absolute;top:13.5px;left:0;width:0;height:2px;border-radius:2px;background:#041e3a;transition:transform .18s ease}',
    /* Bar is 2px at rest; hover and drag take it to 3px */
    '@media (hover:hover){tt-scrollbar:hover .tt-scrollbar__thumb{transform:scaleY(1.5)}}',
    'tt-scrollbar.is-dragging .tt-scrollbar__thumb{transform:scaleY(1.5)}',
    /* While the bar moves the row, card snapping is off. A class, not an inline style: the theme's
       carousel clears inline snap settings every time scrolling pauses, which made the row jump a
       whole card at a time (the "flashing"). */
    '.tt-gliding{scroll-snap-type:none!important;scroll-behavior:auto!important}',
    /* Homepage: tight bottom while a bar shows (the section sets its padding by id, hence !important) */
    '.shopify-section--featured-collections .section-spacing:has(tt-scrollbar:not([hidden])){padding-block-end:7px!important}',
    /* Homepage mobile arrows: 44px tap target, drawn the same size */
    '@media screen and (max-width:699px){.shopify-section--featured-collections [class*="cfc-header-arrows-"]{gap:0!important;margin-inline-end:-6px}.shopify-section--featured-collections [class*="cfc-header-arrows-"] .prev-next-button{width:44px;height:44px}}'
  ].join('\n');
  var style = document.createElement('style');
  style.id = 'tt-scrollbar-style';
  style.textContent = css;
  document.head.appendChild(style);

  /* ---------- The bar ---------- */
  class TTScrollbar extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.thumb = this.querySelector('.tt-scrollbar__thumb');
      this.carouselId = this.getAttribute('for');
      this.carousel = document.getElementById(this.carouselId);
      if (!this.carousel || !this.thumb) return;
      this.ready = true;

      this.grab = 0;
      this.dragging = false;
      this.update = this.update.bind(this);
      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);

      this.carousel.addEventListener('scroll', this.update, { passive: true });
      this.addEventListener('pointerdown', this.onDown);
      this.addEventListener('pointermove', this.onMove);
      this.addEventListener('pointerup', this.onUp);
      this.addEventListener('pointercancel', this.onUp);

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.update);
        this.resizeObserver.observe(this.carousel);
      } else {
        window.addEventListener('resize', this.update);
      }

      /* Cards and images arrive after first paint, so check again shortly after */
      this.update();
      [150, 600, 1500, 3000].forEach((t) => setTimeout(this.update, t));
    }

    disconnectedCallback() {
      if (this.carousel) this.carousel.removeEventListener('scroll', this.update);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.update);
      this.ready = false;
    }

    metrics() {
      const c = this.carousel;
      const max = c.scrollWidth - c.clientWidth;
      const ratio = c.scrollWidth > 0 ? c.clientWidth / c.scrollWidth : 1;
      return { max, ratio };
    }

    toggleArrows(show) {
      document
        .querySelectorAll('carousel-prev-button[aria-controls="' + this.carouselId + '"], carousel-next-button[aria-controls="' + this.carouselId + '"]')
        .forEach((el) => { el.style.display = show ? '' : 'none'; });
    }

    layout() {
      /* Keep the line exactly 19px under the bottom of the cards. Take back out any space the
         carousel keeps under its cards (padding) and any row gap the parent adds above the bar. */
      const parent = this.parentElement;
      if (parent) {
        const ps = getComputedStyle(parent);
        const stacked = ps.display.indexOf('grid') > -1 || (ps.display.indexOf('flex') > -1 && ps.flexDirection.indexOf('column') > -1);
        const gap = stacked ? parseFloat(ps.rowGap) || 0 : 0;

        /* Measured with offsetHeight, not screen position, so the cards' fade-up animation on
           page load can't throw it off */
        let extra = 0;
        const holder = this.previousElementSibling;
        if (holder) {
          let cardsHeight = 0;
          const items = this.carousel.children;
          for (let i = 0; i < items.length && i < 12; i++) {
            if (items[i].offsetWidth > 0) cardsHeight = Math.max(cardsHeight, items[i].offsetHeight);
          }
          if (cardsHeight > 0) {
            const cs = getComputedStyle(this.carousel);
            const cardsBottom = this.carousel.getBoundingClientRect().top + (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.paddingTop) || 0) + cardsHeight;
            extra = Math.max(0, holder.getBoundingClientRect().bottom - cardsBottom);
          }
        }
        this.style.marginTop = (5 - gap - extra) + 'px';
      }

      /* Homepage bars: line up with the section's header text */
      if (this.dataset.alignHeader === 'true' && parent) {
        const section = this.closest('.shopify-section');
        const header = section && section.querySelector('[class*="cfc-header-row-"]');
        if (header && header.offsetWidth > 0) {
          const hs = getComputedStyle(header);
          const hr = header.getBoundingClientRect();
          const pr = parent.getBoundingClientRect();
          const left = hr.left + (parseFloat(hs.paddingLeft) || 0) - pr.left;
          const right = pr.right - (hr.right - (parseFloat(hs.paddingRight) || 0));
          this.style.marginLeft = Math.max(0, left) + 'px';
          this.style.marginRight = Math.max(0, right) + 'px';
        }
      }
    }

    update() {
      const c = this.carousel;
      if (!c || !c.isConnected) return;
      /* A carousel that isn't on screen (e.g. the desktop copy on a phone) has no size: stay hidden */
      if (c.clientWidth === 0) { this.hidden = true; return; }

      const { max, ratio } = this.metrics();
      const scrollable = max > 2;
      this.hidden = !scrollable;
      this.toggleArrows(scrollable);
      if (!scrollable) return;

      this.layout();
      const pos = Math.min(1, Math.max(0, Math.abs(c.scrollLeft) / max));
      const width = Math.max(8, ratio * 100);
      this.thumb.style.width = width + '%';
      this.thumb.style.left = pos * (100 - width) + '%';
    }

    /*
      Smooth dragging
      A short bar on a long row is high-geared: on a phone, 1px of finger can be ~20px of row, which
      reads as flashing. So the finger sets a TARGET and the row glides toward it each frame
      (eased follow), instead of jumping. Touch eases more than mouse. On release the row finishes
      gliding, then settles gently onto the nearest card. Each frame's move is also capped, so a hard
      flick still glides. Reduced-motion users get direct movement.
    */
    onDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const thumbRect = this.thumb.getBoundingClientRect();
      const onThumb = event.clientX >= thumbRect.left && event.clientX <= thumbRect.right;
      this.grab = onThumb ? event.clientX - thumbRect.left : thumbRect.width / 2;
      this.rect = this.getBoundingClientRect();
      this.thumbWidth = thumbRect.width;
      this.dragging = true;
      this.settling = false;
      this.classList.add('is-dragging');

      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.ease = reduced ? 1 : (event.pointerType === 'mouse' ? 0.3 : 0.14);
      /* Speed limit per frame (about 60 frames a second): even a hard flick glides, never whips */
      this.maxStep = reduced ? Infinity : (event.pointerType === 'mouse' ? 140 : 70);

      /* Snap points fight a drag; switch them off until the row has settled */
      clearTimeout(this.snapTimer);
      this.carousel.classList.add('tt-gliding');
      this.pos = this.carousel.scrollLeft;
      try { this.setPointerCapture(event.pointerId); } catch (e) {}
      event.preventDefault();
      this.moveTo(event.clientX);
    }

    onMove(event) {
      if (this.dragging) this.moveTo(event.clientX);
    }

    onUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.classList.remove('is-dragging');
      try { this.releasePointerCapture(event.pointerId); } catch (e) {}
      /* The glide loop keeps running to the target, then calls settle() */
      if (!this.frame) this.settle();
    }

    moveTo(x) {
      const span = this.rect.width - this.thumbWidth;
      let p = span > 0 ? (x - this.rect.left - this.grab) / span : 0;
      p = Math.min(1, Math.max(0, p));
      this.target = p * this.metrics().max;
      if (!this.frame) this.frame = requestAnimationFrame(() => this.glide());
    }

    glide() {
      this.frame = null;
      const c = this.carousel;
      const diff = this.target - this.pos;
      if (Math.abs(diff) < 0.5) {
        this.pos = this.target;
        c.scrollLeft = this.pos;
        if (this.dragging) return;
        if (this.settling) {
          this.settling = false;
          c.classList.remove('tt-gliding');
        } else {
          this.settle();
        }
        return;
      }
      let step = diff * this.ease;
      if (Math.abs(step) > this.maxStep) step = Math.sign(step) * this.maxStep;
      this.pos += step;
      c.scrollLeft = this.pos;
      this.frame = requestAnimationFrame(() => this.glide());
    }

    settle() {
      const c = this.carousel;
      const max = this.metrics().max;
      /* Nearest card edge, so we land where the arrows and swipes would land */
      let best = c.scrollLeft;
      let bestDist = Infinity;
      const base = c.getBoundingClientRect().left + (parseFloat(getComputedStyle(c).scrollPaddingLeft) || 0);
      for (const item of c.children) {
        if (!item.offsetWidth) continue;
        const left = Math.min(max, Math.max(0, c.scrollLeft + item.getBoundingClientRect().left - base));
        const d = Math.abs(left - c.scrollLeft);
        if (d < bestDist) { bestDist = d; best = left; }
      }
      if (this.ease === 1 || Math.abs(best - c.scrollLeft) < 1) {
        c.scrollLeft = best;
        c.classList.remove('tt-gliding');
        return;
      }
      /* Same eased glide for the last few pixels onto the card edge, then snapping back on */
      this.target = best;
      this.pos = c.scrollLeft;
      this.settling = true;
      this.frame = requestAnimationFrame(() => this.glide());
    }
  }

  window.customElements.define('tt-scrollbar', TTScrollbar);

  /* ---------- Homepage: attach a bar under each featured-collections carousel ---------- */
  function autoAttach(root) {
    (root || document).querySelectorAll('.shopify-section--featured-collections scroll-carousel[id]').forEach(function (carousel) {
      if (document.querySelector('tt-scrollbar[for="' + carousel.id + '"]')) return;
      var holder = carousel.closest('product-list') || carousel;
      var bar = document.createElement('tt-scrollbar');
      bar.setAttribute('for', carousel.id);
      bar.setAttribute('aria-hidden', 'true');
      bar.dataset.alignHeader = 'true';
      bar.hidden = true;
      /* Keep the section's desktop-only / mobile-only switch on the bar too */
      ['cfc-show-desktop', 'cfc-show-mobile'].forEach(function (k) {
        if (holder.classList.contains(k)) bar.classList.add(k);
      });
      bar.innerHTML = '<span class="tt-scrollbar__track"></span><span class="tt-scrollbar__thumb"></span>';
      holder.after(bar);
    });
  }

  autoAttach();
  /* Theme editor: sections re-render in place */
  document.addEventListener('shopify:section:load', function (e) { autoAttach(e.target); });
})();
